import {
  DefaultFetchError,
  FetchCreate,
  FetchDelete,
  FetchGet,
  FetchGetId,
  FetchSuccess,
  FetchUpdate,
  PagingDataResponse,
  PagingParams,
  useFetchCreate,
  useFetchDelete,
  useFetchGet,
  useFetchGetId,
  useFetchUpdate,
} from "../fetch";

export type ReadingType = "pdf" | "doc";

export interface ReadingVaad {
  id: string;
  name: string;
}

export interface ReadingMeeting {
  id: string;
  name: string;
}

export interface Reading {
  id: string;
  name: string;
  type: ReadingType;
  fileLink: string;
  vaad: ReadingVaad;
  meeting: ReadingMeeting;
  globalContent: boolean;
  middahs: { id: string; name: string }[];
  createdAt: string;
  updatedAt: string;
}

export interface ReadingTableRow extends Reading {
  key: string;
}

export interface ReadingTableData {
  data: ReadingTableRow[];
  total: number;
}

export interface ReadingCreateParams {
  name: string;
  type: ReadingType;
  fileLink: string;
  middahs?: { id: string }[];
  globalContent?: boolean;
}

export interface ReadingUpdateParams {
  name?: string;
  type?: ReadingType;
  fileLink?: string;
  vaad?: { id: string; name?: string };
  middahs?: { id: string }[];
  globalContent?: boolean;
}

export interface ReadingGetParams
  extends PagingParams, Partial<Omit<Reading, "id" | "vaad" | "meeting">> {
  orderByColumn?: keyof Omit<Reading, "id" | "vaad" | "meeting">;
  type?: ReadingType;
  vaadId?: string;
  meetingId?: string;
}
export const useReadingTypesGet = (): FetchGet<
  string[],
  void,
  DefaultFetchError,
  string[]
> =>
  useFetchGet("readings/enum/types", {
    autoStart: true,
  });

export const useReadingGet = <DD = PagingDataResponse<Reading>>(
  decorateData?: (data: PagingDataResponse<Reading>) => DD,
): FetchGet<
  PagingDataResponse<Reading>,
  ReadingGetParams,
  DefaultFetchError,
  DD
> =>
  useFetchGet("readings", {
    autoStart: false,
    startStateLoading: false,
    decorateData,
  });

export const useReadingTableGet = () =>
  useReadingGet<ReadingTableData>(
    ({ data, meta }: PagingDataResponse<Reading>): ReadingTableData => ({
      data: data.map((item) => ({
        key: item.id,
        ...item,
      })),
      total: meta.itemCount,
    }),
  );

export const useReadingGetById = <DD = Reading>(
  id?: string,
  decorateData?: (data: Reading) => DD,
): FetchGetId<Reading, DefaultFetchError, unknown, DD> =>
  useFetchGetId("readings", id, {
    autoStart: !!id,
    decorateData,
  });

export const useReadingCreate = (): FetchCreate<
  ReadingCreateParams,
  DefaultFetchError,
  ReadingCreateParams
> => useFetchCreate("readings");

export const useReadingUpdate = (
  id?: string,
): FetchUpdate<ReadingUpdateParams, DefaultFetchError, ReadingUpdateParams> =>
  useFetchUpdate("readings", id);

export const useReadingsDelete = (
  id?: string,
): FetchDelete<FetchSuccess, DefaultFetchError> =>
  useFetchDelete("readings", id);
